import React, { useEffect, useState, useCallback } from "react";
import { auditApi, errorMessage } from "../adminApi";
import { Card, Table, Badge, Button, Loading, ErrorState, PageHeader } from "../components/ui";
import { formatDateTime } from "../format";

const LIMIT = 50;

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ action: "", entity: "", date_from: "", date_to: "" });

  const load = useCallback(async (pageArg = 0, f = filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: LIMIT, offset: pageArg * LIMIT };
      Object.entries(f).forEach(([k, v]) => v && (params[k] = v));
      const res = await auditApi.list(params);
      setRows(res.rows || res || []);
      setTotal(res.total ?? (res.rows || res || []).length);
      setPage(pageArg);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(0);
  }, [load]);

  const setF = (k) => (e) => setFilters((prev) => ({ ...prev, [k]: e.target.value }));

  const columns = [
    { key: "time", header: "Waktu", render: (r) => <span className="whitespace-nowrap text-xs">{formatDateTime(r.created_at)}</span> },
    { key: "actor", header: "Pelaku", render: (r) => r.actor_username || (r.actor_id ? String(r.actor_id).slice(0, 8) : "system") },
    { key: "action", header: "Aksi", render: (r) => <Badge color="blue">{r.action}</Badge> },
    { key: "entity", header: "Entity", render: (r) => <span className="text-xs">{r.entity}{r.entity_id ? ` #${String(r.entity_id).slice(0, 8)}` : ""}</span> },
    { key: "ip", header: "IP", render: (r) => <span className="text-xs text-slate-400">{r.ip_address || "-"}</span> },
    {
      key: "detail",
      header: "Detail",
      render: (r) => (
        <span className="text-xs text-slate-500 font-mono block max-w-[240px] truncate" title={JSON.stringify(r.detail)}>
          {r.detail ? JSON.stringify(r.detail) : "-"}
        </span>
      ),
    },
  ];

  const maxPage = Math.max(0, Math.ceil(total / LIMIT) - 1);

  return (
    <div className="h-full flex flex-col">
      <PageHeader title="Audit Log" subtitle="Jejak semua aksi sensitif di sistem." />

      <Card className="p-3 mb-4 shrink-0">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 items-end">
          <FilterInput label="Aksi" value={filters.action} onChange={setF("action")} placeholder="mis. APPROVE_LEAVE" />
          <FilterInput label="Entity" value={filters.entity} onChange={setF("entity")} placeholder="mis. users" />
          <FilterInput label="Dari" type="date" value={filters.date_from} onChange={setF("date_from")} />
          <FilterInput label="Sampai" type="date" value={filters.date_to} onChange={setF("date_to")} />
          <Button onClick={() => load(0)}>Terapkan</Button>
        </div>
      </Card>

      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(0)} />
        ) : (
          <>
            <Card className="flex-1 min-h-0 overflow-hidden">
              <Table columns={columns} rows={rows} empty="Tidak ada log." />
            </Card>
            <div className="flex items-center justify-between mt-3 text-sm text-slate-500 shrink-0">
              <span>Total: {total}</span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 0} onClick={() => load(page - 1)}>← Sebelumnya</Button>
                <span className="px-2 py-1.5">Hal {page + 1} / {maxPage + 1}</span>
                <Button variant="secondary" size="sm" disabled={page >= maxPage} onClick={() => load(page + 1)}>Berikutnya →</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FilterInput({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-slate-500 mb-1">{label}</span>
      <input className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" {...props} />
    </label>
  );
}
